import { useCallback, useState } from 'react';

let lastKey = 0;
function newKey(): string {
  lastKey += 1;
  return `row-${lastKey}`;
}
const newKeys = (n: number): string[] => Array.from({ length: n }, () => newKey());

export interface RowKeys {
  /** One React key per row, in row order. */
  keys: string[];
  /** Record that a row was added at the end. */
  appended: () => void;
  /** Record that row `i` was removed. */
  removed: (i: number) => void;
  /** Record that the row at `from` moved to `to`. */
  moved: (from: number, to: number) => void;
}

/**
 * React keys for the rows of a list whose items carry no id of their own
 * (strings, numbers, pairs, id groups). A key is made when its row is added
 * and travels with the row through a move or the removal of another row, so
 * each row keeps its own DOM node and component state. The keys live beside
 * the value, never in it.
 *
 * The caller records each change it makes (appended / removed / moved) in the
 * same event as its onChange. When the list's length changes any other way
 * (a paste, a parent reset), the rows it still has keep their keys by
 * position and any new rows get new keys.
 */
export function useRowKeys(length: number): RowKeys {
  const [keys, setKeys] = useState(() => newKeys(length));
  let current = keys;
  if (keys.length !== length) {
    current = keys.length > length ? keys.slice(0, length) : [...keys, ...newKeys(length - keys.length)];
    setKeys(current);
  }
  const appended = useCallback(() => setKeys((k) => [...k, newKey()]), []);
  const removed = useCallback((i: number) => setKeys((k) => k.filter((_, j) => j !== i)), []);
  const moved = useCallback(
    (from: number, to: number) =>
      setKeys((k) => {
        const next = k.slice();
        const [picked] = next.splice(from, 1);
        next.splice(to, 0, picked);
        return next;
      }),
    [],
  );
  return { keys: current, appended, removed, moved };
}
